import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  Briefcase, DollarSign, CheckCircle,
  Calendar, Activity, XCircle, Clock
} from 'lucide-react';
import {
  getClientSubscriptions,
  requestSubscriptionCancellation,
} from '../functions/portalBillingApi';
import { format } from 'date-fns';
import {
  KpiCard,
  StatusBadge,
  PageHeader,
  EmptyState,
  Spinner,
} from './portal/PortalUI';

const GOOD_STANDING = new Set(['active', 'trialing']);

const ClientPortalServices = () => {
  const queryClient = useQueryClient();
  // Which subscription the client is confirming cancellation of. Requesting is a
  // real decision, so it takes two clicks, not one.
  const [confirmingId, setConfirmingId] = useState(null);

  const {
    data: subscriptions,
    isLoading,
    isError,
  } = useQuery('clientSubscriptions', getClientSubscriptions, {
    // Keep the portal fresh without manual refresh (updates land via webhooks).
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchInterval: 30 * 1000,
    retry: 1,
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'PP');
  };

  const formatCurrency = (amountCents, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format((amountCents || 0) / 100);
  };

  const formatServiceName = (serviceCode) => {
    if (!serviceCode) return 'Subscription';
    return serviceCode
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getStatusBadgeProps = (status) => {
    if (GOOD_STANDING.has(status)) {
      return { status: 'active', label: 'Active' };
    }
    return { status, label: (status || 'unknown').replace(/_/g, ' ') };
  };

  const cancelRequest = useMutation(
    (subscriptionId) => requestSubscriptionCancellation(subscriptionId),
    {
      onSuccess: (data) => {
        setConfirmingId(null);
        queryClient.invalidateQueries('clientSubscriptions');
        toast.success(
          data?.message ||
            'We received your request and will be in touch. Your service stays active until then.'
        );
      },
      onError: (error) => {
        setConfirmingId(null);
        toast.error(error?.message || 'Could not submit your cancellation request');
      },
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner label="Loading your services…" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error loading services. Please try again later.</p>
      </div>
    );
  }

  const items = subscriptions || [];
  const activeCount = items.filter((s) => GOOD_STANDING.has(s.status)).length;
  const needsAttentionCount = items.length - activeCount;
  const totalRecurringByCurrency = items
    .filter((s) => GOOD_STANDING.has(s.status))
    .reduce((totals, s) => {
      const currency = (s.currency || 'usd').toUpperCase();
      totals[currency] = (totals[currency] || 0) + (s.amount || 0);
      return totals;
    }, {});

  const upcomingRenewals = items
    .filter((s) => GOOD_STANDING.has(s.status) && s.current_period_end)
    .sort((a, b) => new Date(a.current_period_end) - new Date(b.current_period_end))
    .slice(0, 3);

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        title="Services"
        subtitle="View and manage your active services and subscriptions"
      />

      {/* Service Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5 mb-10">
        <KpiCard
          title="Active Services"
          value={activeCount}
          icon={CheckCircle}
          tone="positive"
        />
        <KpiCard
          title="Needs Attention"
          value={needsAttentionCount}
          icon={Activity}
          tone="attention"
          delay={0.05}
        />
        <KpiCard
          title="Total Active Value"
          value={
            Object.keys(totalRecurringByCurrency).length === 0
              ? formatCurrency(0)
              : Object.entries(totalRecurringByCurrency)
                  .map(([currency, cents]) => formatCurrency(cents, currency))
                  .join(' + ')
          }
          icon={DollarSign}
          tone="neutral"
          delay={0.1}
          className="col-span-2 md:col-span-1"
        />
      </div>

      {/* Active Services Section */}
      <div className="mb-12">
        <h2 className="font-display text-lg sm:text-xl font-bold text-grayteck-900 dark:text-grayteck-100 mb-5">
          Your Subscriptions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((service, index) => {
            const badgeProps = getStatusBadgeProps(service.status);
            return (
              <motion.div
                key={service.stripe_subscription_id || index}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
                className="bg-white dark:bg-darkbg-800 rounded-xl border border-grayteck-200 dark:border-darkbg-600 shadow-card dark:shadow-card-dark overflow-hidden hover:shadow-card-lg transition-shadow"
              >
                <div className="p-4 sm:p-6">
                  <div className="flex justify-between items-start gap-3 mb-4">
                    <div className="min-w-0">
                      <h3 className="font-display text-base sm:text-lg font-bold text-grayteck-900 dark:text-grayteck-100 truncate">
                        {formatServiceName(service.service_code)}
                      </h3>
                      <p className="mt-0.5 text-sm text-grayteck-700 dark:text-grayteck-400">
                        {formatCurrency(service.amount, service.currency)}
                        <span className="text-grayteck-500 dark:text-grayteck-500"> / {service.interval}</span>
                      </p>
                    </div>
                    <StatusBadge
                      status={badgeProps.status}
                      label={badgeProps.label}
                      className="capitalize shrink-0"
                    />
                  </div>

                  <div className="space-y-2 pt-3 border-t border-grayteck-200 dark:border-darkbg-600">
                    {service.current_period_end && (
                      <div className="flex items-center text-sm text-grayteck-700 dark:text-grayteck-400">
                        <Calendar className="w-4 h-4 mr-2 text-grayteck-500 dark:text-grayteck-500" />
                        <span>Next billing {formatDate(service.current_period_end)}</span>
                      </div>
                    )}
                    {service.cancel_at_period_end && (
                      <div className="flex items-center text-sm font-semibold text-red-600 dark:text-red-400">
                        <XCircle className="w-4 h-4 mr-2" />
                        <span>Cancels at end of current period</span>
                      </div>
                    )}
                  </div>

                  {/* Cancellation. Requesting does not cancel: it reaches a human,
                      who cancels after speaking with the client. So the copy must
                      not imply the service has stopped. */}
                  {GOOD_STANDING.has(service.status) && (
                    <div className="mt-4 pt-3 border-t border-grayteck-200 dark:border-darkbg-600">
                      {service.cancellation_requested_at ? (
                        <div className="flex items-start text-sm text-grayteck-700 dark:text-grayteck-400">
                          <Clock className="w-4 h-4 mr-2 mt-0.5 shrink-0 text-yellow-500" />
                          <span>
                            Cancellation requested on{' '}
                            {formatDate(service.cancellation_requested_at)}. We'll be in
                            touch. Your service stays active until then.
                          </span>
                        </div>
                      ) : confirmingId === service.stripe_subscription_id ? (
                        <div>
                          <p className="text-sm text-grayteck-700 dark:text-grayteck-300 mb-3">
                            Request cancellation of{' '}
                            <span className="font-semibold">
                              {formatServiceName(service.service_code)}
                            </span>
                            ? We'll reach out to confirm. Nothing changes until we speak.
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                cancelRequest.mutate(service.stripe_subscription_id)
                              }
                              disabled={cancelRequest.isLoading}
                              className="px-3 py-1.5 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 transition-colors"
                            >
                              {cancelRequest.isLoading ? 'Sending…' : 'Yes, request it'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmingId(null)}
                              disabled={cancelRequest.isLoading}
                              className="px-3 py-1.5 rounded-md text-sm font-medium text-grayteck-700 dark:text-grayteck-300 hover:bg-grayteck-100 dark:hover:bg-darkbg-700 transition-colors"
                            >
                              Never mind
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmingId(service.stripe_subscription_id)}
                          className="text-sm font-medium text-grayteck-600 dark:text-grayteck-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        >
                          Request cancellation
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {items.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-darkbg-800 rounded-xl border border-grayteck-200 dark:border-darkbg-600 shadow-card dark:shadow-card-dark"
          >
            <EmptyState icon={Briefcase} title="No Active Services">
              You currently don't have any active services. Contact your account
              manager to learn more about our offerings.
            </EmptyState>
          </motion.div>
        )}
      </div>

      {/* Upcoming Renewals */}
      {upcomingRenewals.length > 0 && (
        <div className="mt-12">
          <h2 className="font-display text-lg sm:text-xl font-bold text-grayteck-900 dark:text-grayteck-100 mb-5">
            Upcoming Renewals
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
            {upcomingRenewals.map((service, index) => (
              <motion.div
                key={service.stripe_subscription_id || index}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
                className="bg-white dark:bg-darkbg-800 p-4 sm:p-5 rounded-xl border border-grayteck-200 dark:border-darkbg-600 shadow-card dark:shadow-card-dark"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-grayteck-100 dark:bg-darkbg-700">
                    <Calendar className="w-5 h-5 text-grayteck-600 dark:text-grayteck-300" />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-caps text-grayteck-600 dark:text-grayteck-400">
                    {formatDate(service.current_period_end)}
                  </span>
                </div>
                <h3 className="font-display text-base font-bold text-grayteck-900 dark:text-grayteck-100 mb-1">
                  {formatServiceName(service.service_code)}
                </h3>
                <p className="text-sm text-grayteck-700 dark:text-grayteck-400">
                  Next {service.interval} payment{' '}
                  <span className="font-semibold text-grayteck-800 dark:text-grayteck-200">
                    {formatCurrency(service.amount, service.currency)}
                  </span>
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortalServices;
